import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  BookOpen,
  Landmark,
  FileSpreadsheet,
  GitCompareArrows,
  PiggyBank,
  Send,
  FileSignature,
  Building2,
  Sparkles,
  Clock,
  ShieldCheck,
  Smartphone,
  FileDown,
  ScanLine,
  ArrowRight,
  Menu,
  X,
  Linkedin,
  Twitter,
  Instagram,
  Mail,
  MapPin,
  Phone,
  CheckCircle2,
} from "lucide-react";
import { api, extractError } from "../lib/api";
import { toast } from "sonner";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/1363ee4c-1e4f-4365-bf64-33dfa884cc2a/images/2d6564ae087c9647426c239dfe00091ec4bf5ecc099610a03ccc0545d84abda5.png";
const FEAT_REPORTS = "https://static.prod-images.emergentagent.com/jobs/1363ee4c-1e4f-4365-bf64-33dfa884cc2a/images/4098ee40cbf83e77033ac4e75d484ef237460944a8c2a40263e36a77e985a202.png";
const FEAT_BANK = "https://static.prod-images.emergentagent.com/jobs/1363ee4c-1e4f-4365-bf64-33dfa884cc2a/images/4e877e5a35900810c53f01a3efddaecde67342a1b8d2a7f6dbed656b045e649a.png";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

const stagger = {
  initial: {},
  whileInView: {},
  viewport: { once: true, margin: "-80px" },
  transition: { staggerChildren: 0.08 },
};

// ============================================================================
// NAVBAR
// ============================================================================
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#caracteristicas", label: "Características" },
    { href: "#modulos", label: "Módulos" },
    { href: "#beneficios", label: "Beneficios" },
    { href: "#contacto", label: "Contacto" },
  ];

  return (
    <header
      data-testid="landing-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-black/5 shadow-[0_1px_0_rgba(0,0,0,0.04)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#1B4332] to-[#2D6A4F] flex items-center justify-center text-white font-display font-bold text-sm shadow-sm group-hover:scale-105 transition-transform">
            A
          </div>
          <span className="font-display font-bold tracking-tighter text-lg text-[#0F172A]">
            Aequor
          </span>
        </Link>

        {/* Desktop links */}
        <nav className="hidden md:flex items-center gap-8 text-[14px] font-medium text-[#0F172A]/75">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="hover:text-[#1B4332] transition-colors relative after:absolute after:inset-x-0 after:-bottom-1 after:h-0.5 after:bg-[#1B4332] after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/login"
            data-testid="navbar-login-btn"
            className="inline-flex items-center gap-1.5 bg-[#1B4332] hover:bg-[#143325] text-white text-sm font-medium px-4 py-2 rounded-full transition-all hover:scale-105 shadow-sm"
          >
            Ingresar al panel <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
          </Link>
        </div>

        {/* Mobile menu button */}
        <button
          onClick={() => setOpen(!open)}
          data-testid="navbar-mobile-menu-btn"
          className="md:hidden p-2 rounded-md hover:bg-black/5 text-[#0F172A]"
          aria-label="Menú"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile dropdown */}
      {open && (
        <div className="md:hidden bg-white border-t border-black/5 px-6 py-4 space-y-3" data-testid="navbar-mobile-dropdown">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block py-2 text-[#0F172A]/75 hover:text-[#1B4332]"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/login"
            className="block text-center bg-[#1B4332] hover:bg-[#143325] text-white text-sm font-medium px-4 py-2.5 rounded-full mt-2"
          >
            Ingresar al panel
          </Link>
        </div>
      )}
    </header>
  );
}

// ============================================================================
// HERO
// ============================================================================
function Hero() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 400], [0, -50]);
  return (
    <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden">
      {/* Background gradient + grain */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#FAFAF9] via-[#FAFAF9] to-[#D9F99D]/30" />
      <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#06B6D4]/10 via-[#D9F99D]/20 to-transparent blur-3xl -z-10" />
      <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-[#1B4332]/8 via-transparent to-transparent blur-3xl -z-10" />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left text */}
        <motion.div {...fadeUp}>
          <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-[#1B4332]/15 rounded-full px-3 py-1 text-[12px] font-medium text-[#1B4332] mb-6">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
            Para contadores y estudios · Una o muchas empresas
          </div>

          <h1 className="font-display font-black tracking-tighter text-[#0F172A] text-[42px] sm:text-[56px] lg:text-[68px] leading-[0.95]">
            El ERP contable{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#1B4332] via-[#2D6A4F] to-[#06B6D4]">
              hecho a tu medida
            </span>
          </h1>

          <p className="text-[#0F172A]/65 text-lg sm:text-xl mt-6 max-w-xl leading-relaxed">
            Aequor concentra en un solo panel lo que antes te llevaba horas: libros de compra y venta
            desde el SII, asientos automáticos, conciliación bancaria, honorarios, remuneraciones y
            reportes. Menús claros, funciones fáciles de encontrar y todo a pocos clics — ya sea que
            lleves una empresa o decenas de clientes.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link
              to="/login"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center justify-center gap-2 bg-[#1B4332] hover:bg-[#143325] text-white text-[15px] font-medium px-6 py-3.5 rounded-full transition-all hover:scale-[1.02] shadow-lg shadow-[#1B4332]/20"
            >
              Ingresar al panel
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.5} />
            </Link>
            <a
              href="#modulos"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-white/80 border border-[#0F172A]/10 text-[#0F172A] text-[15px] font-medium px-6 py-3.5 rounded-full transition-all hover:scale-[1.02]"
            >
              Ver módulos
            </a>
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-8 text-[13px] text-[#0F172A]/60">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#1B4332]" strokeWidth={2} /> Multi-empresa desde un solo acceso</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#1B4332]" strokeWidth={2} /> Plan de cuentas y normativa local</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#1B4332]" strokeWidth={2} /> Interfaz simple, sin curva de aprendizaje</span>
          </div>
        </motion.div>

        {/* Right illustration */}
        <motion.div
          style={{ y: heroY }}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          className="relative"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#06B6D4]/20 via-[#D9F99D]/30 to-[#1B4332]/10 blur-2xl rounded-3xl" />
          <div className="relative bg-white/40 backdrop-blur-xl border border-white/60 rounded-3xl p-3 shadow-2xl">
            <img
              src={HERO_IMG}
              alt="Dashboard Aequor"
              className="w-full rounded-2xl"
              loading="lazy"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// STATS STRIP
// ============================================================================
function StatsStrip() {
  const stats = [
    { value: "1 panel", label: "para todas tus empresas y RUTs" },
    { value: "RCV SII", label: "importación de compras y ventas en minutos" },
    { value: "Pocos clics", label: "desde la factura hasta el asiento contable" },
    { value: "PDF + Excel", label: "reportes listos para entregar al cliente" },
  ];
  return (
    <section id="caracteristicas" className="bg-gradient-to-br from-[#1B4332] via-[#143325] to-[#0F2E20] text-white relative overflow-hidden" data-testid="stats-strip">
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_30%,#D9F99D,transparent_50%)]" />
      <motion.div
        {...stagger}
        className="max-w-7xl mx-auto px-6 sm:px-8 py-14 md:py-16 grid grid-cols-2 md:grid-cols-4 gap-8 relative"
      >
        {stats.map((s) => (
          <motion.div key={s.label} variants={fadeUp} className="text-center md:text-left">
            <div className="font-display font-black tracking-tighter text-3xl md:text-5xl mb-1.5">{s.value}</div>
            <div className="text-white/60 text-[13px] md:text-sm leading-snug">{s.label}</div>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}

// ============================================================================
// FEATURES (BENTO)
// ============================================================================
const MODULES = [
  {
    icon: BookOpen,
    title: "Contabilidad completa y ordenada",
    desc: "Libro Diario, Mayor, Balance General, Balance de 8 columnas y Estado de Resultados en el mismo lugar. Plan de cuentas listo para usar, asientos claros y reportes que puedes exportar cuando los necesites.",
    image: FEAT_REPORTS,
    span: "md:col-span-8 lg:col-span-8",
    tone: "primary",
  },
  {
    icon: ScanLine,
    title: "Libros de compra y venta (SII)",
    desc: "Sube el CSV o Excel del Registro de Compras y Ventas: el sistema lee las columnas del SII, crea proveedores o clientes si faltan y deja las facturas listas para contabilizar o revisar.",
    span: "md:col-span-4 lg:col-span-4",
    tone: "accent",
  },
  {
    icon: GitCompareArrows,
    title: "Tesorería y conciliación bancaria",
    desc: "Carga cartolas en CSV o Excel, concilia movimientos con tu libro en pocos pasos y mantén varias cuentas bancarias por empresa sin perder el control.",
    image: FEAT_BANK,
    span: "md:col-span-6 lg:col-span-6",
    tone: "primary",
  },
  {
    icon: Building2,
    title: "Multi-empresa para estudios y contadores",
    desc: "Cambia de RUT en segundos, usuarios con permisos por módulo y un panel por organización. Pensado para quien trabaja con muchas empresas a la vez.",
    span: "md:col-span-6 lg:col-span-6",
    tone: "default",
  },
  {
    icon: PiggyBank,
    title: "Honorarios, pagos, caja chica y remuneraciones",
    desc: "Boletas de honorarios con retención calculada, pagos a proveedores, caja chica con arqueo, liquidaciones de sueldo e importación de dotación desde Excel — cada operación puede generar su asiento contable.",
    span: "md:col-span-12 lg:col-span-12",
    tone: "wide",
    icons: [PiggyBank, Send, FileSignature],
  },
];

function Features() {
  return (
    <section id="modulos" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <motion.div {...fadeUp} className="max-w-2xl mb-14 md:mb-20">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#1B4332] mb-3">Módulos</div>
          <h2 className="font-display font-black tracking-tighter text-[#0F172A] text-4xl md:text-5xl leading-[1.05]">
            Cada función en su lugar. Sin adivinar dónde está nada.
          </h2>
          <p className="text-[#0F172A]/65 text-lg mt-5">
            No es un ERP genérico adaptado a la fuerza: los módulos siguen el flujo real del contador
            chileno — desde el RCV del SII hasta el informe para la gerencia — con pantallas directas
            y acciones que se entienden a la primera.
          </p>
        </motion.div>

        <motion.div {...stagger} className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-5 md:gap-6">
          {MODULES.map((m, idx) => (
            <motion.div
              key={idx}
              variants={fadeUp}
              className={`${m.span} bg-white border border-black/5 rounded-2xl p-7 md:p-8 hover:-translate-y-1 hover:shadow-xl hover:border-[#1B4332]/20 transition-all duration-300 relative overflow-hidden group`}
              data-testid={`feature-${idx}`}
            >
              <div className={`inline-flex h-11 w-11 rounded-xl items-center justify-center mb-5 ${
                m.tone === "primary" ? "bg-[#1B4332] text-white" :
                m.tone === "accent" ? "bg-[#D9F99D] text-[#1B4332]" :
                m.tone === "wide" ? "bg-gradient-to-br from-[#1B4332] to-[#06B6D4] text-white" :
                "bg-[#FAFAF9] border border-black/5 text-[#1B4332]"
              }`}>
                {m.icons ? (
                  <div className="flex gap-1">
                    {m.icons.map((Ic, i) => <Ic key={i} className="h-4 w-4" strokeWidth={2} />)}
                  </div>
                ) : (
                  <m.icon className="h-5 w-5" strokeWidth={1.75} />
                )}
              </div>
              <h3 className="font-display font-bold tracking-tight text-[#0F172A] text-xl md:text-2xl mb-2">{m.title}</h3>
              <p className="text-[#0F172A]/60 text-[15px] leading-relaxed">{m.desc}</p>
              {m.image && (
                <div className="mt-6 -mx-7 md:-mx-8 -mb-7 md:-mb-8">
                  <img src={m.image} alt="" loading="lazy" className="w-full opacity-90 group-hover:opacity-100 transition-opacity" />
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// BENEFITS
// ============================================================================
const BENEFITS = [
  {
    icon: Clock,
    title: "Horas de trabajo en minutos",
    desc: "Importa cientos de documentos del SII, genera asientos en lote y concilia la cartola sin repetir el mismo proceso empresa por empresa.",
  },
  {
    icon: Building2,
    title: "Muchas empresas, un solo login",
    desc: "Ideal para estudios contables y contadores independientes: organiza clientes por organización, cambia de empresa al instante y mantén todo separado.",
  },
  {
    icon: ShieldCheck,
    title: "Contabilidad que parte bien armada",
    desc: "Plan de cuentas coherente con la práctica local, IVA crédito y débito, honorarios y estructura lista para el trabajo diario.",
  },
  {
    icon: FileDown,
    title: "Información para decidir",
    desc: "Balance, estado de resultados, proyección de impuestos e informe ejecutivo en PDF para mostrar resultados con claridad a tus clientes.",
  },
  {
    icon: GitCompareArrows,
    title: "Tesorería bajo control",
    desc: "Sabe qué está conciliado y qué no, con reglas claras entre el banco y tu libro — menos sorpresas al cierre de mes.",
  },
  {
    icon: Smartphone,
    title: "Cómodo en cualquier pantalla",
    desc: "Revisa saldos, aprueba movimientos o consulta un reporte desde el notebook o el celular, con una interfaz legible y sin saturar.",
  },
];

function Benefits() {
  return (
    <section id="beneficios" className="py-24 md:py-32 bg-gradient-to-b from-[#FAFAF9] to-white relative">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <motion.div {...fadeUp} className="max-w-2xl mb-14">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#1B4332] mb-3">Beneficios</div>
          <h2 className="font-display font-black tracking-tighter text-[#0F172A] text-4xl md:text-5xl leading-[1.05]">
            Simple de entender. Potente cuando lo necesitas.
          </h2>
          <p className="text-[#0F172A]/65 text-lg mt-5 max-w-2xl">
            Aequor no compite por tener mil menús ocultos: prioriza lo que un contador usa cada mes,
            con rutas claras y menos fricción entre una tarea y la siguiente.
          </p>
        </motion.div>
        <motion.div {...stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {BENEFITS.map((b, idx) => (
            <motion.div
              key={idx}
              variants={fadeUp}
              className="bg-white border border-black/5 rounded-2xl p-6 hover:border-[#1B4332]/20 hover:-translate-y-1 transition-all duration-300"
              data-testid={`benefit-${idx}`}
            >
              <div className="h-10 w-10 rounded-xl bg-[#D9F99D]/40 border border-[#1B4332]/10 flex items-center justify-center mb-4">
                <b.icon className="h-5 w-5 text-[#1B4332]" strokeWidth={1.75} />
              </div>
              <h3 className="font-display font-bold text-[#0F172A] text-lg mb-1.5 tracking-tight">{b.title}</h3>
              <p className="text-[#0F172A]/60 text-[14px] leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// CTA STRIP
// ============================================================================
function CtaStrip() {
  return (
    <section className="py-20">
      <div className="max-w-5xl mx-auto px-6 sm:px-8">
        <motion.div
          {...fadeUp}
          className="relative bg-gradient-to-br from-[#1B4332] via-[#143325] to-[#0F2E20] rounded-3xl p-10 md:p-14 text-white overflow-hidden"
        >
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#06B6D4]/20 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-[#D9F99D]/15 blur-3xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#D9F99D] mb-3">Acceso anticipado</div>
              <h3 className="font-display font-black tracking-tighter text-3xl md:text-4xl leading-tight">
                Lleva tu contabilidad con menos estrés y más control.
              </h3>
              <p className="text-white/70 mt-3 text-[15px]">
                Ingresa al panel, configura tu empresa o las de tus clientes y empieza por importar
                tu primer libro del SII — verás el flujo en acción en minutos.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-[#FAFAF9] text-[#1B4332] text-[15px] font-medium px-6 py-3.5 rounded-full transition-all hover:scale-105"
              >
                Ingresar al panel <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </Link>
              <a
                href="#contacto"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-[15px] font-medium px-6 py-3.5 rounded-full transition-all"
              >
                Hablar con ventas
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ============================================================================
// CONTACT
// ============================================================================
function Contact() {
  const [form, setForm] = useState({ nombre: "", email: "", empresa: "", mensaje: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("¡Gracias! Te contactaremos pronto.");
      setSent(true);
      setForm({ nombre: "", email: "", empresa: "", mensaje: "" });
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" className="py-24 md:py-32 bg-white relative">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <motion.div {...fadeUp}>
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#1B4332] mb-3">Contacto</div>
          <h2 className="font-display font-black tracking-tighter text-[#0F172A] text-4xl md:text-5xl leading-[1.05]">
            ¿Quieres ver Aequor con tu operación?
          </h2>
          <p className="text-[#0F172A]/65 text-lg mt-5">
            Cuéntanos si eres estudio contable, empresa o consultor. Te respondemos con una demo
            adaptada a tu volumen de empresas y a los módulos que más te interesan.
          </p>

          <ul className="space-y-4 mt-10 text-[14px]">
            <li className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-[#1B4332] mt-0.5 shrink-0" strokeWidth={1.75} />
              <div>
                <div className="text-[#0F172A]/50 text-[11px] uppercase tracking-[0.15em]">Email</div>
                <a href="mailto:hola@aequor.cl" className="font-medium text-[#0F172A] hover:text-[#1B4332]">hola@aequor.cl</a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Phone className="h-5 w-5 text-[#1B4332] mt-0.5 shrink-0" strokeWidth={1.75} />
              <div>
                <div className="text-[#0F172A]/50 text-[11px] uppercase tracking-[0.15em]">Teléfono</div>
                <span className="font-medium text-[#0F172A]">+56 2 2000 0000</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-[#1B4332] mt-0.5 shrink-0" strokeWidth={1.75} />
              <div>
                <div className="text-[#0F172A]/50 text-[11px] uppercase tracking-[0.15em]">Oficina</div>
                <span className="font-medium text-[#0F172A]">Santiago, Chile</span>
              </div>
            </li>
          </ul>
        </motion.div>

        <motion.form
          {...fadeUp}
          onSubmit={submit}
          className="bg-[#FAFAF9] border border-black/5 rounded-3xl p-7 md:p-9"
          data-testid="contact-form"
        >
          {sent ? (
            <div className="text-center py-12">
              <div className="inline-flex h-14 w-14 rounded-full bg-[#1B4332]/10 items-center justify-center mb-4">
                <CheckCircle2 className="h-7 w-7 text-[#1B4332]" strokeWidth={2} />
              </div>
              <div className="font-display font-bold text-2xl text-[#0F172A] mb-2 tracking-tight">¡Mensaje recibido!</div>
              <p className="text-[#0F172A]/60">Te contactaremos a la brevedad.</p>
              <button
                type="button"
                onClick={() => setSent(false)}
                className="mt-6 text-[14px] text-[#1B4332] font-medium hover:underline"
              >
                Enviar otro mensaje
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-[11px] font-bold tracking-[0.15em] uppercase text-[#0F172A]/60 mb-1.5 block">Nombre</label>
                <input
                  required
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  data-testid="contact-nombre"
                  className="w-full bg-white border border-black/8 rounded-xl px-4 py-3 text-[14px] focus:outline-none focus:border-[#1B4332] focus:ring-2 focus:ring-[#1B4332]/10 transition"
                  placeholder="Tu nombre"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] font-bold tracking-[0.15em] uppercase text-[#0F172A]/60 mb-1.5 block">Email</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="contact-email"
                    className="w-full bg-white border border-black/8 rounded-xl px-4 py-3 text-[14px] focus:outline-none focus:border-[#1B4332] focus:ring-2 focus:ring-[#1B4332]/10 transition"
                    placeholder="tu@empresa.cl"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold tracking-[0.15em] uppercase text-[#0F172A]/60 mb-1.5 block">Empresa</label>
                  <input
                    value={form.empresa}
                    onChange={(e) => setForm({ ...form, empresa: e.target.value })}
                    data-testid="contact-empresa"
                    className="w-full bg-white border border-black/8 rounded-xl px-4 py-3 text-[14px] focus:outline-none focus:border-[#1B4332] focus:ring-2 focus:ring-[#1B4332]/10 transition"
                    placeholder="(opcional)"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold tracking-[0.15em] uppercase text-[#0F172A]/60 mb-1.5 block">Mensaje</label>
                <textarea
                  required
                  rows={4}
                  value={form.mensaje}
                  onChange={(e) => setForm({ ...form, mensaje: e.target.value })}
                  data-testid="contact-mensaje"
                  className="w-full bg-white border border-black/8 rounded-xl px-4 py-3 text-[14px] focus:outline-none focus:border-[#1B4332] focus:ring-2 focus:ring-[#1B4332]/10 transition resize-none"
                  placeholder="Cuéntanos brevemente qué necesitas…"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#1B4332] hover:bg-[#143325] disabled:opacity-60 text-white text-[15px] font-medium px-6 py-3.5 rounded-xl transition-all hover:scale-[1.01] shadow-lg shadow-[#1B4332]/20 mt-2"
              >
                {sending ? "Enviando…" : <>Enviar mensaje <ArrowRight className="h-4 w-4" strokeWidth={2.5} /></>}
              </button>
            </div>
          )}
        </motion.form>
      </div>
    </section>
  );
}

// ============================================================================
// FOOTER
// ============================================================================
function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-[#0F172A] text-white pt-16 pb-8" data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#2D6A4F] to-[#06B6D4] flex items-center justify-center font-display font-bold text-sm">A</div>
              <span className="font-display font-bold tracking-tighter text-lg">Aequor</span>
            </div>
            <p className="text-white/55 text-[13px] leading-relaxed max-w-[240px]">
              ERP contable en la nube: claro, multi-empresa y pensado para que el contador trabaje
              más rápido, con todo al alcance de unos clics.
            </p>
          </div>

          <div>
            <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-white/40 mb-4">Producto</div>
            <ul className="space-y-2.5 text-[13px] text-white/70">
              <li><a href="#modulos" className="hover:text-white">Módulos</a></li>
              <li><a href="#beneficios" className="hover:text-white">Beneficios</a></li>
              <li><Link to="/login" className="hover:text-white">Ingresar al panel</Link></li>
            </ul>
          </div>

          <div>
            <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-white/40 mb-4">Empresa</div>
            <ul className="space-y-2.5 text-[13px] text-white/70">
              <li><a href="#contacto" className="hover:text-white">Contacto</a></li>
              <li><a href="#" className="hover:text-white">Acerca de</a></li>
              <li><a href="#" className="hover:text-white">Blog</a></li>
            </ul>
          </div>

          <div>
            <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-white/40 mb-4">Legal</div>
            <ul className="space-y-2.5 text-[13px] text-white/70">
              <li><a href="#" className="hover:text-white">Términos</a></li>
              <li><a href="#" className="hover:text-white">Privacidad</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[12px] text-white/45 text-center sm:text-left">
            © {year} Aequor. Todos los derechos reservados.
            <span className="hidden sm:inline"> · </span>
            <span className="block sm:inline text-white/35">aequor.cl</span>
          </p>
          <div className="flex items-center gap-3" data-testid="footer-social">
            {[
              { Icon: Linkedin, href: "#", label: "LinkedIn" },
              { Icon: Twitter, href: "#", label: "Twitter / X" },
              { Icon: Instagram, href: "#", label: "Instagram" },
            ].map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                aria-label={label}
                className="h-9 w-9 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 flex items-center justify-center transition-colors"
              >
                <Icon className="h-4 w-4 text-white/70" strokeWidth={1.75} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

// ============================================================================
// PAGE
// ============================================================================
export default function Landing() {
  return (
    <div className="font-sans bg-[#FAFAF9] text-[#0F172A] overflow-x-hidden" data-testid="landing-page">
      <Navbar />
      <Hero />
      <StatsStrip />
      <Features />
      <Benefits />
      <CtaStrip />
      <Contact />
      <Footer />
    </div>
  );
}
